/**
 * Screen 10 — bulk Excel import.
 *
 * Two phases, deliberately: **upload validates, commit creates**. A
 * half-imported batch of legal documents is worse than a rejected one, so
 * nothing exists until the user has seen the row-by-row result and confirmed it.
 *
 * The per-row errors are the same `partyErrors` structure the create form
 * produces — one validator, two callers — so this screen renders them with the
 * same component rather than a second, subtly different one. A row cannot get
 * through the importer that the form would have refused.
 *
 * `errores.xlsx` closes the loop: the failed rows come back in the template's
 * own shape with an appended column, so the fix happens in Excel and the file is
 * re-imported.
 */

import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { unwrap, type ImportRow } from '@deca/api'

import { api, downloadFile, MIME } from '../app/api.ts'
import { apiErrorMessage } from '../app/errors.ts'
import {
  qk,
  useImportJob,
  useImportRows,
  useImports,
  useInvalidateDeca,
  type ImportRowStatus,
} from '../app/queries.ts'
import { useAuth } from '../app/auth.tsx'
import { abilitiesFor } from '../deca/roles.ts'
import { offeredFamilies } from '../deca/transportType.ts'
import { TEMPLATE_COLUMNS } from '../deca/fields.ts'
import { formatDateTime } from '../deca/format.ts'
import { AppShell } from '../shell/AppShell.tsx'
import {
  Button,
  Card,
  Chip,
  EmptyState,
  LoadingRows,
  NoAccessState,
  Notice,
  ProgressBar,
} from '../ui/primitives.tsx'
import { Icon } from '../ui/Icon.tsx'
import { PassengerValidationSummary } from '../ui/PassengerValidationSummary.tsx'
import { ValidationSummary } from '../ui/ValidationSummary.tsx'
import { useToast } from '../ui/Toast.tsx'
import { ImportSummaryDialog } from './ImportSummaryDialog.tsx'
import { importSearchSchema } from './searchSchemas.ts'

const ROW_TONE = {
  VALID: 'info',
  ERROR: 'danger',
  CREATED: 'success',
  SKIPPED: 'neutral',
} as const

export function ImportScreen() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const toast = useToast()
  const invalidate = useInvalidateDeca()
  const { principal } = useAuth()
  const abilities = abilitiesFor(principal)
  const offered = offeredFamilies(principal)

  const rawSearch = useSearch({ strict: false })
  const { job: jobId } = importSearchSchema.parse(rawSearch)

  const job = useImportJob(jobId)
  const previous = useImports()

  const [rowFilter, setRowFilter] = useState<ImportRowStatus | undefined>(undefined)
  const rows = useImportRows(jobId, rowFilter, Boolean(jobId))

  const [uploading, setUploading] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [generate, setGenerate] = useState(true)
  const [skipErrorRows, setSkipErrorRows] = useState(true)
  const [committing, setCommitting] = useState(false)
  const fileInput = useRef<HTMLInputElement>(null)
  const client = useQueryClient()

  const status = job.data?.status

  /**
   * The summary opens on a commit this screen sees finish (#229): one it started, or one already
   * `COMMITTING` when the screen opened. An import opened finished, from «Importaciones anteriores»,
   * shows none. `watching` is the job whose commit is awaited, and a commit started here sets it at
   * once: the worker can finish before the next read of the job ever shows `COMMITTING`.
   */
  const watching = useRef<string | null>(null)
  const [summaryFor, setSummaryFor] = useState<string | null>(null)
  useEffect(() => {
    if (!jobId) return
    if (status === 'COMMITTING') watching.current = jobId
    else if ((status === 'COMPLETED' || status === 'FAILED') && watching.current === jobId) {
      watching.current = null
      // The table's rows were read before the commit finished.
      void client.invalidateQueries({ queryKey: [...qk.import(jobId), 'rows'] })
      setSummaryFor(jobId)
    }
  }, [client, jobId, status])

  if (!abilities.writeDecas) {
    return (
      <AppShell title={t('import.title')}>
        <Card>
          <NoAccessState />
        </Card>
      </AppShell>
    )
  }

  async function upload(file: File) {
    setUploading(true)
    setError(null)
    try {
      const created = await unwrap(
        api.POST('/api/v1/imports', {
          // `format: binary` generates as `string`, so the File is cast here and
          // handed to FormData by the serializer below. The cast is confined to
          // this one call rather than spread through a hand-rolled uploader.
          body: { file: file as unknown as string },
          bodySerializer(body: { file: string }) {
            const form = new FormData()
            form.append('file', body.file as unknown as File)
            return form
          },
        }),
      )
      void navigate({ to: '/importar', search: { job: created.id } })
    } catch (caught) {
      setError(apiErrorMessage(caught, t))
    } finally {
      setUploading(false)
    }
  }

  async function commit() {
    if (!jobId) return
    setCommitting(true)
    setError(null)
    try {
      await unwrap(
        api.POST('/api/v1/imports/{importId}/commit', {
          params: { path: { importId: jobId } },
          body: { generate, skipErrorRows },
        }),
      )
      watching.current = jobId
      invalidate()
      void job.refetch()
      void rows.refetch()
      toast.success(t('import.committing'))
    } catch (caught) {
      setError(apiErrorMessage(caught, t))
    } finally {
      setCommitting(false)
    }
  }

  const working = status === 'UPLOADED' || status === 'VALIDATING' || status === 'COMMITTING'

  /**
   * Which of the three steps the job is on. Upload validates and commit
   * creates, so "Resultados" is reached only once something has been created —
   * the frame draws created rows under a still-pending commit button, which is
   * the one thing about it that cannot be true.
   */
  const step = !jobId ? 1 : status === 'COMPLETED' || status === 'COMMITTING' ? 3 : 2

  /**
   * Two templates, one screen (#80).
   *
   * The upload itself asks nothing — it recognises the family from the file's own header row, the
   * two sharing no column name — so this is the only place the operator has to choose, and they
   * choose by picking which empty workbook to start from. A company offered one type (#220) is
   * offered its own template alone; the upload still takes either family's file, as the API does.
   */
  const downloadTemplate = (kind: 'mercancias' | 'viajeros' = 'mercancias') =>
    void downloadFile(
      kind === 'viajeros' ? '/api/v1/imports/template?tipo=viajeros' : '/api/v1/imports/template',
      MIME.xlsx,
      kind === 'viajeros'
        ? 'Plantilla_importacion_Hoja_de_Ruta.xlsx'
        : 'Plantilla_importacion_DeCA.xlsx',
    )

  return (
    <AppShell title={t('import.title')}>
      <Stepper current={step} />

      {error ? <Notice tone="danger">{error}</Notice> : null}

      {!jobId ? (
        <>
          <Card>
            <div className="stack">
              <button
                type="button"
                className="dropzone"
                data-over={dragOver}
                data-disabled={uploading}
                disabled={uploading}
                onClick={() => fileInput.current?.click()}
                onDragOver={(event) => {
                  event.preventDefault()
                  setDragOver(true)
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(event) => {
                  event.preventDefault()
                  setDragOver(false)
                  const file = event.dataTransfer.files.item(0)
                  if (file) void upload(file)
                }}
              >
                <div className="state__icon">
                  <Icon name="upload" size={24} />
                </div>
                <span className="state__title">
                  {uploading ? t('import.uploading') : t('import.dropzone')}
                </span>
                <span className="small muted">{t('import.dropzoneHint')}</span>
              </button>

              <input
                ref={fileInput}
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                hidden
                onChange={(event) => {
                  const file = event.target.files?.item(0)
                  if (file) void upload(file)
                  event.target.value = ''
                }}
              />

              {uploading ? <ProgressBar /> : null}

              {/* Wraps: on a phone each template takes a full-width line (screens.css). */}
              <div className="row wrap import-templates">
                {offered.goods ? (
                  <Button icon="download" onClick={() => downloadTemplate('mercancias')}>
                    {t('import.downloadTemplateGoods')}
                  </Button>
                ) : null}
                {offered.passenger ? (
                  <Button icon="download" variant="secondary" onClick={() => downloadTemplate('viajeros')}>
                    {t('import.downloadTemplatePassenger')}
                  </Button>
                ) : null}
              </div>

              <details>
                <summary className="small muted import-columns__summary">{t('import.columnsTitle')}</summary>
                <p className="small mono" style={{ marginTop: 'var(--space-sm)' }}>
                  {TEMPLATE_COLUMNS.join(' · ')}
                </p>
              </details>
            </div>
          </Card>

          <Card title={t('import.history')} flush>
            {previous.isLoading ? (
              <LoadingRows rows={3} columns={4} />
            ) : (previous.data?.data ?? []).length === 0 ? (
              <EmptyState icon="upload" title={t('state.emptyTitle')} />
            ) : (
              // Stacked on a phone, each cell under its column's name: `data-label` repeats the
              // header, which is hidden there (`.table--stack`, styles/responsive/document.css).
              <div className="table-wrap">
                <table className="table table--stack">
                  <caption className="sr-only">{t('import.history')}</caption>
                  <thead>
                    <tr>
                      <th scope="col">{t('common.date')}</th>
                      <th scope="col">{t('import.jobStatus')}</th>
                      <th scope="col" className="table__num">
                        {t('import.rowCount')}
                      </th>
                      <th scope="col" className="table__num">
                        {t('import.createdCount')}
                      </th>
                      <th scope="col" />
                    </tr>
                  </thead>
                  <tbody>
                    {(previous.data?.data ?? []).map((item) => (
                      <tr key={item.id}>
                        <td className="tabular" data-label={t('common.date')}>
                          {formatDateTime(item.createdAt, i18n.language)}
                        </td>
                        <td data-label={t('import.jobStatus')}>{t(`import.jobStatusValue.${item.status}`)}</td>
                        <td className="table__num" data-label={t('import.rowCount')}>
                          {item.rowCount ?? 0}
                        </td>
                        <td className="table__num" data-label={t('import.createdCount')}>
                          {item.createdCount ?? 0}
                        </td>
                        {/* Its header is empty, and so is its label: the button names itself. */}
                        <td className="table__actions" data-label="">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => void navigate({ to: '/importar', search: { job: item.id } })}
                          >
                            {t('import.reviewTitle')}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      ) : (
        <>
          <Card>
            <div className="stack">
              {/* The file, its counters and the way back — one line, as the
                  frame has it, instead of four KPI tiles. */}
              <div className="file-card">
                <span className="file-card__icon" aria-hidden="true">
                  <Icon name="file" size={20} />
                </span>
                <span className="file-card__body">
                  <span className="file-card__name">{job.data?.filename ?? t('import.title')}</span>
                  <span className="small muted">
                    {t('import.fileMeta', { count: job.data?.rowCount ?? 0 })}
                  </span>
                </span>
                <button
                  type="button"
                  className="link-button"
                  onClick={() => void navigate({ to: '/importar', search: {} })}
                >
                  {t('import.change')}
                </button>

                <span className="spacer" />

                <Chip tone="success">
                  {t('import.validCountInline', { count: job.data?.validCount ?? 0 })}
                </Chip>
                <Chip tone="danger">
                  {t('import.errorCountInline', { count: job.data?.errorCount ?? 0 })}
                </Chip>
                <span className="small muted tabular">
                  {t('import.totalInline', { count: job.data?.rowCount ?? 0 })}
                </span>
                <button type="button" className="link-button" onClick={() => downloadTemplate('mercancias')}>
                  {t('import.downloadTemplate')}
                </button>
              </div>

              {working ? (
                <>
                  <Notice tone="info">
                    {status === 'COMMITTING' ? t('import.committing') : t('import.validating')}
                  </Notice>
                  <ProgressBar />
                </>
              ) : null}

              {job.data?.error ? <Notice tone="danger">{job.data.error}</Notice> : null}

              {status === 'VALIDATED' ? (
                <div className="stack-sm">
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      checked={generate}
                      onChange={(event) => setGenerate(event.currentTarget.checked)}
                    />
                    {t('import.commitGenerate')}
                  </label>
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      checked={skipErrorRows}
                      onChange={(event) => setSkipErrorRows(event.currentTarget.checked)}
                    />
                    {skipErrorRows ? t('import.commitSkipErrors') : t('import.commitStrict')}
                  </label>
                  <div className="row wrap">
                    <Button
                      variant="primary"
                      icon="check"
                      loading={committing}
                      disabled={committing}
                      onClick={() => void commit()}
                    >
                      {t('import.commit')}
                    </Button>
                    {(job.data?.errorCount ?? 0) > 0 ? (
                      <Button
                        icon="download"
                        onClick={() =>
                          void downloadFile(
                            `/api/v1/imports/${encodeURIComponent(jobId)}/errors.xlsx`,
                            MIME.xlsx,
                            'errores.xlsx',
                          )
                        }
                      >
                        {t('import.downloadErrors')}
                      </Button>
                    ) : null}
                  </div>
                  <p className="small muted">{t('import.downloadErrorsHelp')}</p>
                </div>
              ) : null}
            </div>
          </Card>

          <Card
            title={t('import.reviewTitle')}
            subtitle={`${t('import.reviewHelp')} ${t('party.penaltyExplain', { amount: 401 })}`}
            flush
          >
            <div className="filter-bar">
              <button
                type="button"
                className="pill"
                aria-pressed={rowFilter === undefined}
                onClick={() => setRowFilter(undefined)}
              >
                {t('import.filterAll')}
              </button>
              {(['ERROR', 'VALID', 'CREATED', 'SKIPPED'] as const).map((value) => (
                <button
                  key={value}
                  type="button"
                  className="pill"
                  aria-pressed={rowFilter === value}
                  onClick={() => setRowFilter(value)}
                >
                  {t(`import.rowStatus.${value}`)}
                </button>
              ))}
            </div>

            {rows.isLoading ? (
              <LoadingRows rows={5} columns={3} />
            ) : (rows.data?.data ?? []).length === 0 ? (
              <EmptyState icon="file" title={t('state.emptyTitle')} />
            ) : (
              // Stacked on a phone, as the history table is. The column widths are classes rather
              // than inline styles, so a phone rule can undo them (screens.css).
              <div className="table-wrap">
                <table className="table table--stack">
                  <caption className="sr-only">{t('import.reviewTitle')}</caption>
                  <thead>
                    <tr>
                      <th scope="col" className="import-col-row">
                        {t('import.row')}
                      </th>
                      <th scope="col" className="import-col-status">
                        {t('import.jobStatus')}
                      </th>
                      <th scope="col">{t('import.reviewTitle')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(rows.data?.data ?? []).map((row) => (
                      <ImportRowView key={row.rowNumber} row={row} passenger={job.data?.kind === 'PASSENGER'} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}

      {summaryFor !== null && summaryFor === jobId && job.data ? (
        <ImportSummaryDialog job={job.data} onClose={() => setSummaryFor(null)} />
      ) : null}
    </AppShell>
  )
}

function ImportRowView({ row, passenger }: { row: ImportRow; passenger: boolean }) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  return (
    <tr data-row-status={row.status}>
      <td className="tabular" data-label={t('import.row')}>
        {row.rowNumber}
      </td>
      <td data-label={t('import.jobStatus')}>
        <Chip
          tone={ROW_TONE[row.status]}
          icon={row.status === 'ERROR' ? 'alert' : row.status === 'CREATED' ? 'checkCircle' : 'file'}
        >
          {t(`import.rowStatus.${row.status}`)}
        </Chip>
      </td>
      <td data-label={t('import.reviewTitle')}>
        {row.status === 'ERROR' ? (
          <RowReasons row={row} />
        ) : row.status === 'CREATED' && row.documentId ? (
          <div className="row wrap">
            <span className="tabular">{row.documentNumber}</span>
            {/* A route sheet opens on its own screen (#229): the id names no DeCA. */}
            <Button
              size="sm"
              variant="ghost"
              icon="externalLink"
              onClick={() =>
                void (passenger
                  ? navigate({ to: '/hojas-de-ruta/$sheetId', params: { sheetId: row.documentId! } })
                  : navigate({ to: '/decas/$decaId', params: { decaId: row.documentId! } }))
              }
            >
              {passenger
                ? t('import.openSheet', { documentNumber: row.documentNumber ?? '' })
                : t('list.openDetail', { documentNumber: row.documentNumber ?? '' })}
            </Button>
          </div>
        ) : (
          <span className="small muted truncate import-preview">{previewOf(row)}</span>
        )}
      </td>
    </tr>
  )
}

/**
 * Why a row was not created (#229): the upload's verdict, by party on goods and flat on a route
 * sheet, and the commit's own message when the commit failed the row.
 */
function RowReasons({ row }: { row: ImportRow }) {
  return (
    <div className="stack-sm">
      {row.fieldErrors?.length ? (
        <PassengerValidationSummary errors={row.fieldErrors} compact />
      ) : (
        <ValidationSummary partyErrors={row.partyErrors} compact />
      )}
      {row.errorMessage ? <span className="small">{row.errorMessage}</span> : null}
    </div>
  )
}

/** The normalised values, in template column order, for the review table. */
function previewOf(row: ImportRow): string {
  const preview = row.preview ?? {}
  return TEMPLATE_COLUMNS.map((column) => preview[column])
    .filter((value) => value !== undefined && value !== null && value !== '')
    .join(' · ')
}

/** Subir archivo → Validar filas → Resultados. */
function Stepper({ current }: { current: number }) {
  const { t } = useTranslation()
  const steps = [t('import.stepUpload'), t('import.stepValidate'), t('import.stepResults')]

  return (
    <ol className="stepper">
      {steps.map((label, index) => {
        const number = index + 1
        const state = number < current ? 'done' : number === current ? 'current' : 'todo'
        return (
          <li key={label} className="stepper__step" data-state={state}>
            <span className="stepper__marker" aria-hidden="true">
              {state === 'done' ? <Icon name="check" size={14} /> : number}
            </span>
            <span>{label}</span>
          </li>
        )
      })}
    </ol>
  )
}
